import { useCallback, useEffect } from 'react';
import { useWizard } from '../../../components/wizard/useWizard';
import { Form } from 'antd';
import {
	FeedbackWithConfirmation,
	UserRole,
} from '../../../shared/VolunteerApplicationServiceTypes';
import { ReviewAction } from '../../../shared/Constants';
import { useTranslation } from 'react-i18next';
import useUserSession from '../../../hooks/useUserSession';
import { ALLOWED_ROLES } from '../../../shared/Utils';

export default function useCategoryFeedbackFormHandling(
	categoryValue: FeedbackWithConfirmation,
	setCategoryValue: (value: FeedbackWithConfirmation) => void,
	isLastPage?: boolean
) {
	const {
		setNextButtonDisabled,
		setSubmitButtonDisabled,
		setSubmitButtonText,
	} = useWizard();
	const { t } = useTranslation();
	const [form] = Form.useForm<FeedbackWithConfirmation>();
	const values = Form.useWatch<FeedbackWithConfirmation>([], form);
	const { selectedRole } = useUserSession();

	const isRoleNotAllowed = !ALLOWED_ROLES.includes(selectedRole as UserRole);

	useEffect(() => {
		if (isRoleNotAllowed) {
			setSubmitButtonText(t('done'));
		} else {
			setSubmitButtonText(t('saveAndContinue'));
		}
	}, [isRoleNotAllowed, setSubmitButtonText, t]);

	useEffect(() => {
		if (
			values &&
			values.review === ReviewAction.APPROVE &&
			values.videoCallCheckbox
		) {
			setSubmitButtonDisabled(false);
		} else {
			setSubmitButtonDisabled(true);
		}
	}, [setSubmitButtonDisabled, values]);

	const handleFormValuesChange = useCallback(async () => {
		try {
			const fieldValues = await form.validateFields({ validateOnly: true });
			setCategoryValue(fieldValues);
			if (isLastPage) {
				setSubmitButtonDisabled(false);
			} else {
				setNextButtonDisabled(false);
			}
		} catch (e) {
			const fields = e as { values: FeedbackWithConfirmation };
			setCategoryValue(fields.values);
			if (isLastPage) {
				setSubmitButtonDisabled(true);
			} else {
				setNextButtonDisabled(true);
			}
		}
	}, [
		form,
		isLastPage,
		setCategoryValue,
		setNextButtonDisabled,
		setSubmitButtonDisabled,
	]);

	useEffect(() => {
		void handleFormValuesChange();
	}, [handleFormValuesChange, values]);

	useEffect(() => {
		form.setFieldsValue(categoryValue);
		form.setFieldsValue({
			videoCallCheckbox: categoryValue.review === ReviewAction.APPROVE,
		});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [form]);

	return { form };
}
